import { CouponType } from '/component/Economy/Coupons/types';
import { uuidValidationSchema } from '/helpers';

export const sandboxPathName = 'sandbox';
export const livePathName = 'live';
export const createPathName = 'create';
export const editPathName = 'edit';

export const capitalizeFirstLetter = (word: string): string => {
  const trimmed = word.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

export const isUUID = (value: string): boolean => uuidValidationSchema.isValidSync(value);

export const isId = (value: string): boolean => isUUID(value) || !isNaN(+value);

export const isCoupons = (value: string): boolean => value === CouponType?.targeted || value === CouponType?.mass;

export const isENV = (value: string): boolean => {
  const lower = value.toLowerCase();
  return lower === sandboxPathName || lower === livePathName;
};

export const isEditOrCreate = (value: string): boolean => value === editPathName || value === createPathName;

export const parsePath = (path: string, entityName: string | undefined): string => {
  const segments = path.split('/');
  const [seg4, seg3, seg2, seg1] = segments.slice(-4);

  const last = seg1 || '';
  const penultimate = seg2 || '';
  const prePenultimate = seg3 || '';
  const fourthFromEnd = seg4 || '';

  const parts: string[] = [];

  if (last === createPathName) {
    parts.push('Create');
    if (penultimate) parts.push(capitalizeFirstLetter(penultimate));
    if (prePenultimate && isId(prePenultimate)) {
      parts.unshift(prePenultimate);
    }
    return parts.join(' • ');
  }

  if (last === editPathName) {
    const maybeId = penultimate;
    const maybeModule = isId(maybeId) || isUUID(maybeId) ? prePenultimate : penultimate;

    if (entityName?.trim()) {
      return `${entityName} • ${capitalizeFirstLetter(maybeModule)}`;
    }

    parts.push('Edit');
    parts.push(capitalizeFirstLetter(maybeModule));

    if (isId(prePenultimate)) {
      parts.unshift(prePenultimate);
    } else if (isId(fourthFromEnd)) {
      parts.unshift(fourthFromEnd);
    }

    return parts.join(' • ');
  }

  if (isId(last)) {
    let moduleName: string;

    const penultimateIsIdOrEnvOrCoupons = isId(penultimate) || isENV(penultimate) || isCoupons(penultimate);
    const prePenultimateIsIdOrEnv = isId(prePenultimate) || isENV(prePenultimate);

    if (penultimateIsIdOrEnvOrCoupons) {
      moduleName = prePenultimateIsIdOrEnv ? fourthFromEnd : prePenultimate;
    } else {
      moduleName = penultimate;
    }

    if (entityName?.trim()) {
      if (isCoupons(penultimate)) {
        return `${entityName} • ${capitalizeFirstLetter(penultimate)} • ${capitalizeFirstLetter(moduleName)}`;
      }
      if (isENV(penultimate)) {
        return `${entityName} • ${capitalizeFirstLetter(moduleName)} • ${capitalizeFirstLetter(penultimate)}`;
      }
      return `${entityName} • ${capitalizeFirstLetter(moduleName)}`;
    }

    if (isId(penultimate) || isENV(penultimate)) {
      return isENV(prePenultimate) ? fourthFromEnd : prePenultimate;
    }

    return capitalizeFirstLetter(penultimate);
  }

  if (entityName?.trim() && !isId(last) && !isUUID(last) && !isCoupons(last) && !isEditOrCreate(last)) {
    if (isENV(last)) {
      return `${entityName} • ${capitalizeFirstLetter(prePenultimate)} • ${capitalizeFirstLetter(last)}`;
    }

    return `${entityName} • ${capitalizeFirstLetter(last)}`;
  }

  if (!entityName?.trim() && isENV(last)) {
    const moduleName = !isUUID(penultimate) && !isId(penultimate) ? penultimate : prePenultimate;

    return `${capitalizeFirstLetter(moduleName)} • ${capitalizeFirstLetter(last)}`;
  }

  if (segments.includes('auth')) {
    return 'Google Auth';
  }

  if (isCoupons(last)) {
    return `${capitalizeFirstLetter(last)} • ${capitalizeFirstLetter(penultimate)}`;
  }

  return capitalizeFirstLetter(last);
};
